/**
 * responsive-wrapper
 *
 * Eine Web Component, die abhängig von einem Breakpoint entweder eine Desktop-
 * oder Mobile-Variante rendert. So wird vermieden, dass beide Varianten
 * gleichzeitig im DOM liegen und Performance / A11y negativ beeinflussen.
 *
 * Verwendung:
 *
 * 1. Einfaches Beispiel mit Desktop- und Mobile-Template:
 * <responsive-wrapper breakpoint="1024">
 *   <template id="desktop">
 *     <!-- Desktop-Content -->
 *   </template>
 *   <template id="mobile">
 *     <!-- Mobile-Content -->
 *   </template>
 * </responsive-wrapper>
 *
 * 2. Standard-Breakpoint benutzen (768px):
 * <responsive-wrapper>
 *   <template id="desktop">
 *     <!-- Desktop-Content -->
 *   </template>
 *   <template id="mobile">
 *     <!-- Mobile-Content -->
 *   </template>
 * </responsive-wrapper>
 *
 * Attribute:
 * - breakpoint: Pixel-Breite ab der die Desktop-Variante angezeigt wird
 *               (Number, default: 768)
 *
 * Funktionsweise:
 * - Nutzt window.matchMedia("(min-width: <breakpoint>px)"), um zwischen
 *   Desktop- und Mobile-Ansicht zu wechseln.
 * - Klont beim ersten Rendern nur die aktuell benötigte Variante aus dem
 *   entsprechenden <template>-Element.
 * - Die jeweils andere Variante wird erst erzeugt, wenn der Breakpoint
 *   tatsächlich gewechselt hat.
 * - Nicht aktive Variante wird über das "hidden"-Attribut ausgeblendet.
 *
 * Events:
 * - responsive-wrapper:rendered
 *   - Wird nach jedem Render-Vorgang gefeuert (auch initial).
 *   - detail: { isDesktop: "desktop" | "mobile" }
 *
 */

if (!customElements.get("responsive-wrapper")) {
  class ResponsiveWrapper extends HTMLElement {
    constructor() {
      super();
      this._raf = null;
      this._desktopNode = null;
      this._mobileNode = null;
    }

    get desktopTemplate() {
      return this.querySelector("template#desktop") ?? null;
    }
    get mobileTemplate() {
      return this.querySelector("template#mobile") ?? null;
    }
    get breakpoint() {
      const bp = parseInt(this.getAttribute("breakpoint"), 10);
      return Number.isFinite(bp) ? bp : 768;
    }

    connectedCallback() {
      if (this._inited) return;
      this._inited = true;

      this._mql = window.matchMedia(`(min-width: ${this.breakpoint}px)`);

      // initial render
      this._render();

      // swap nur wenn Breakpoint wechselt (nicht bei jedem resize-pixel)
      this._onChange = () => {
        if (this._raf) cancelAnimationFrame(this._raf);
        this._raf = requestAnimationFrame(() => this._render());
      };

      this._mql.addEventListener("change", this._onChange);
    }

    disconnectedCallback() {
      if (!this._mql || !this._onChange) return;
      this._mql.removeEventListener("change", this._onChange);
      this._onChange = null;
    }

    _createVariantNode(variant, templateEl) {
      if (!templateEl) {
        console.warn(
          `[responsive-wrapper] Missing <template id="${variant}"> inside component.`,
          this,
        );
        return null;
      }
      const root = document.createElement("div");
      root.setAttribute("data-variant", variant);
      root.appendChild(templateEl.content.cloneNode(true));
      return root;
    }

    _render() {
      const variant = this._mql.matches ? "desktop" : "mobile";

      if (variant == "desktop") {
        if (!this._desktopNode && this.desktopTemplate) {
          this._desktopNode = this._createVariantNode(
            "desktop",
            this.desktopTemplate,
          );
          this.appendChild(this._desktopNode);
        }
        if (this._desktopNode) this._desktopNode.hidden = false;
        if (this._mobileNode) this._mobileNode.hidden = true;
      } else {
        if (!this._mobileNode && this.mobileTemplate) {
          this._mobileNode = this._createVariantNode(
            "mobile",
            this.mobileTemplate,
          );
          this.appendChild(this._mobileNode);
        }
        if (this._mobileNode) this._mobileNode.hidden = false;
        if (this._desktopNode) this._desktopNode.hidden = true;
      }

      this.dispatchEvent(
        new CustomEvent("responsive-wrapper:rendered", {
          detail: { variant: variant },
          bubbles: true,
        }),
      );
    }
  }

  customElements.define("responsive-wrapper", ResponsiveWrapper);
}

if (!window.customElements.get("faq-item")) {
  // Globaler Event-Bus für die Kommunikation zwischen Accordion-Elementen
  const faqItemEventBus = new EventTarget();
  class FaqItem extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.getAttribute("section-id");
      this.isOpen = false;
      this.details = this.querySelector("details");
      this.summary = this.querySelector("summary");

      if (this.summary) {
        this.summary.addEventListener("click", () => this.toggle());
      }

      faqItemEventBus.addEventListener("faq-item-opened", (event) => {
        if (
          event.detail !== this &&
          this.isOpen &&
          event.detail.sectionId === this.sectionId
        ) {
          this.close();
        }
      });
    }

    toggle() {
      if (this.isOpen) {
        this.close();
      } else {
        this.open();
        // Informiere andere Accordions, dass dieses geöffnet wurde
        faqItemEventBus.dispatchEvent(
          new CustomEvent("faq-item-opened", { detail: this }),
        );
      }
    }

    open() {
      this.isOpen = true;
      this.details.setAttribute("open", "");
    }

    close() {
      this.isOpen = false;
      this.details.removeAttribute("open");
    }
  }
  window.customElements.define("faq-item", FaqItem);
}

if (!window.customElements.get("product-card-carousel")) {
  class ProductCardCarousel extends HTMLElement {
    constructor() {
      super();
      this.carousel = this.querySelector(".product-card-carousel");
      this.customDots = this.querySelector(".ab-custom-dots");
      this.dots = this.customDots?.querySelectorAll(".dot");

      this.init();
    }

    init() {
      if (!this.carousel || !this.customDots || !this.dots) {
        return;
      }

      // Initialize first dot as active
      this.dots[0].classList.add("active");

      // Set up dot click handlers
      this.setupDotClickHandlers();

      // Listen to carousel change events
      this.setupCarouselEventListeners();
    }

    setActiveDot(index) {
      this.dots.forEach((dot) => dot.classList.remove("active"));
      const dotIndex = index % 3;
      this.dots[dotIndex].classList.add("active");
    }

    setupDotClickHandlers() {
      this.dots.forEach((dot, dotIndex) => {
        dot.addEventListener("click", () => {
          const currentIndex = this.carousel.selectedIndex;
          const groupStart = Math.floor(currentIndex / 3) * 3;
          const targetIndex = groupStart + dotIndex;

          if (targetIndex < this.carousel.cells.length) {
            this.carousel.select(targetIndex);
          }
        });
      });
    }

    setupCarouselEventListeners() {
      // Listen to the carousel's change event
      this.carousel.addEventListener("carousel:change", (event) => {
        const newIndex = event.detail.index;
        this.setActiveDot(newIndex);
      });

      // Also listen to the scroll event for immediate feedback
      this.carousel.addEventListener("scroll", () => {
        // Use the carousel's built-in selectedIndex getter
        const currentIndex = this.carousel.selectedIndex;
        this.setActiveDot(currentIndex);
      });
    }
  }
  window.customElements.define("product-card-carousel", ProductCardCarousel);
}

/**
 * viewport-observer
 *
 * Eine generische Web Component, die beliebigen Content beobachtet und
 * Aktionen ausführt, wenn dieser in/aus dem Viewport kommt.
 *
 * Verwendung:
 *
 * 1. Play/Pause für Videos:
 * <viewport-observer action="play-pause" threshold="0.5" target="video-media">
 *   <video-media autoplay>...</video-media>
 * </viewport-observer>
 *
 * 2. CSS-Klasse hinzufügen/entfernen:
 * <viewport-observer action="add-class" class-name="is-visible" threshold="0.3">
 *   <div class="animate-on-scroll">Content</div>
 * </viewport-observer>
 *
 * 3. Attribute togglen:
 * <viewport-observer action="toggle-attribute" attr-name="visible" threshold="0.7">
 *   <custom-element>Content</custom-element>
 * </viewport-observer>
 *
 * 4. Custom Events verwenden:
 * <viewport-observer id="myObserver">
 *   <div>Content</div>
 * </viewport-observer>
 * <script>
 *   document.getElementById('myObserver').addEventListener('viewport:enter', (e) => {
 *     console.log('Element entered viewport', e.detail.element);
 *   });
 * </script>
 *
 * Attribute:
 * - action: Aktion die ausgeführt werden soll (play-pause, add-class, toggle-attribute, custom)
 * - threshold: Wie viel % des Elements sichtbar sein muss (0.0 - 1.0, default: 0.5)
 * - root-margin: Margin um den Viewport herum (CSS margin syntax, default: "0px")
 * - target: CSS-Selector für das zu beobachtende Element (default: erstes Kind-Element)
 * - class-name: Name der Klasse für action="add-class" (default: "in-viewport")
 * - attr-name: Name des Attributs für action="toggle-attribute" (default: "in-viewport")
 *
 * Events:
 * - viewport:enter: Wird gefeuert wenn Element in den Viewport kommt
 * - viewport:leave: Wird gefeuert wenn Element den Viewport verlässt
 */
if (!window.customElements.get("viewport-observer")) {
  class ViewportObserver extends HTMLElement {
    constructor() {
      super();
      this.intersectionObserver = null;
      this.observedElement = null;
    }

    connectedCallback() {
      // Get the target element to observe (by selector or first child)
      const targetSelector = this.getAttribute("target");
      this.observedElement = targetSelector
        ? this.querySelector(targetSelector)
        : this.firstElementChild;

      if (!this.observedElement) {
        return;
      }

      this.setupIntersectionObserver();
    }

    disconnectedCallback() {
      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
        this.intersectionObserver = null;
      }
    }

    setupIntersectionObserver() {
      // Get configuration from attributes
      const threshold = parseFloat(this.getAttribute("threshold")) || 0.5;
      const rootMargin = this.getAttribute("root-margin") || "0px";
      const action = this.getAttribute("action"); // e.g., "play-pause"

      this.intersectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              // Element is in viewport
              this.handleIntersecting(entry);
            } else {
              // Element is out of viewport
              this.handleNotIntersecting(entry);
            }
          });
        },
        {
          threshold: threshold,
          rootMargin: rootMargin,
        },
      );

      this.intersectionObserver.observe(this.observedElement);
    }

    handleIntersecting(entry) {
      // Dispatch custom event
      this.dispatchEvent(
        new CustomEvent("viewport:enter", {
          detail: { entry, element: this.observedElement },
          bubbles: true,
        }),
      );

      // Execute predefined action
      this.executeAction("enter");
    }

    handleNotIntersecting(entry) {
      // Dispatch custom event
      this.dispatchEvent(
        new CustomEvent("viewport:leave", {
          detail: { entry, element: this.observedElement },
          bubbles: true,
        }),
      );

      // Execute predefined action
      this.executeAction("leave");
    }

    executeAction(state) {
      const action = this.getAttribute("action");

      if (!action) {
        return;
      }

      switch (action) {
        case "play-pause":
          // For video-media or native video elements
          if (state === "enter") {
            if (typeof this.observedElement.play === "function") {
              this.observedElement.play();
            }
          } else {
            if (typeof this.observedElement.pause === "function") {
              this.observedElement.pause();
            }
          }
          break;

        case "add-class":
          // Add/remove class based on viewport state
          const className = this.getAttribute("class-name") || "in-viewport";
          if (state === "enter") {
            this.observedElement.classList.add(className);
          } else {
            this.observedElement.classList.remove(className);
          }
          break;

        case "toggle-attribute":
          // Toggle attribute based on viewport state
          const attrName = this.getAttribute("attr-name") || "in-viewport";
          if (state === "enter") {
            this.observedElement.setAttribute(attrName, "");
          } else {
            this.observedElement.removeAttribute(attrName);
          }
          break;

        default:
          // Custom action - dispatch event with action name
          this.dispatchEvent(
            new CustomEvent(`viewport:${action}:${state}`, {
              detail: { element: this.observedElement },
              bubbles: true,
            }),
          );
          break;
      }
    }
  }
  window.customElements.define("viewport-observer", ViewportObserver);
}

(function () {
  function reinitRatingSnippets() {
    if (typeof window.reinitialiseRatingSnippet === "function") {
      window.reinitialiseRatingSnippet();
    } else if (
      typeof window.ratingSnippet === "function" &&
      window.ratingSnippetGlobalSettings
    ) {
      window.ratingSnippet(
        window.ratingSnippetGlobalSettings.class,
        window.ratingSnippetGlobalSettings.options,
      );
    }
  }

  document.addEventListener("cart-drawer:refreshed", reinitRatingSnippets);

  // "facet:update" fires when the AJAX fetch for pagination/filtering *starts*,
  // not once the new product cards have actually been swapped into the DOM
  // (see the "facet:update" listener in theme.js, which awaits the fetch before
  // calling replaceChildren). Watching for the new .ruk_rating_snippet nodes
  // directly avoids depending on that timing.
  let reinitTimeout = null;
  const ratingSnippetObserver = new MutationObserver((mutations) => {
    const hasNewRatingSnippet = mutations.some((mutation) =>
      Array.from(mutation.addedNodes).some(
        (node) =>
          node.nodeType === Node.ELEMENT_NODE &&
          (node.matches?.(".ruk_rating_snippet") ||
            node.querySelector?.(".ruk_rating_snippet")),
      ),
    );

    if (!hasNewRatingSnippet) return;

    clearTimeout(reinitTimeout);
    reinitTimeout = setTimeout(reinitRatingSnippets, 50);
  });

  ratingSnippetObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });
})();
